import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useActiveEntry } from "@/hooks/use-active-entry";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Trophy, Save, X, Check, ChevronDown, ChevronUp, Trash2, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import {
  useLeagueStrict,
  useTargetUserId,
  useIsOverrideMode,
  useIsWatcher,
} from "@/lib/league-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Flag } from "@/components/Flag";
import { RankBadge } from "@/components/RankBadge";
import { FifaRankingLegend } from "@/components/FifaRankingLegend";
import { LockdownCountdown } from "@/components/LockdownCountdown";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useAutosave } from "@/hooks/use-autosave";
import { useAutosaveAnnouncement } from "@/hooks/use-autosave-announcement";
import { useLockApproach } from "@/hooks/use-lock-approach";
import { AutosaveIndicator } from "@/components/AutosaveIndicator";
import { OverrideSaveDialog } from "@/components/OverrideSaveDialog";
import { ActiveEntryBanner } from "@/components/ActiveEntryBanner";
import { LeagueDisplaySettings } from "@/components/LeagueDisplaySettings";
import { setLastEntryId } from "@/lib/last-entry";
import { listLeagueEntries } from "@/lib/entries.functions";
import {
  getMyGroupPicks,
  saveMyGroupPicks,
  clearAllMyGroupPicks,
  getLeagueGroupStandings,
  type UserGroupRow,
  type UserGroupTeam,
  type GroupMatchPublic,
} from "@/lib/groups.functions";
import { computeGroupStandings, type StandingsRow } from "@/lib/standings";
import {
  computeGroupEngagement,
  summarizeGroupEngagements,
  type GroupEngagement,
} from "@/lib/group-engagement";
import { SaveBarLegalStrip } from "@/components/SaveBarLegalStrip";

import { z } from "zod";

const groupsSearchSchema = z.object({
  entry: z.string().uuid().optional(),
});

export const Route = createFileRoute("/_app/$league/groups")({
  validateSearch: groupsSearchSchema,
  component: GroupsPage,
  head: () => ({
    meta: [
      { title: "Group Stage Picks — Bracket Goblin" },
      {
        name: "description",
        content:
          "Predict the 1st through 4th place finish of every group at the FIFA World Cup.",
      },
    ],
  }),
});

const POSITION_LABEL: Record<number, string> = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
};

type PicksMap = Record<number, string | null>;
const EMPTY_PICKS: PicksMap = { 1: null, 2: null, 3: null, 4: null };

function picksEqual(a: PicksMap, b: PicksMap): boolean {
  for (const p of [1, 2, 3, 4]) {
    if ((a[p] ?? null) !== (b[p] ?? null)) return false;
  }
  return true;
}

/** Stable string of all groups' picks. Used by autosave to detect dirty. */
function sigOfPicks(map: Record<string, PicksMap>): string {
  const ids = Object.keys(map).sort();
  return ids
    .map(
      (id) =>
        `${id}:${[1, 2, 3, 4].map((p) => map[id]?.[p] ?? "").join(",")}`,
    )
    .join("|");
}

function GroupsPage() {
  const league = useLeagueStrict();
  const isGroupFormat = league.tournamentType === "fifa_wc_mens_group_knockout";
  const { entry: entryId } = Route.useSearch();
  const targetUserId = useTargetUserId();
  const isOverride = useIsOverrideMode();
  const isWatcher = useIsWatcher();
  const navigate = useNavigate();

  useEffect(() => {
    if (isWatcher) {
      void navigate({ to: "/$league", params: { league: league.slug }, replace: true });
    }
  }, [isWatcher, league.slug, navigate]);
  if (isWatcher) return null;

  const { activeEntryId: resolvedEntryId, ready: entriesReady } = useActiveEntry({
    leagueId: league.id,
    isOverride,
    targetUserId,
    pinnedEntryId: entryId,
  });
  useEffect(() => {
    if (!entriesReady) return;
    if (entryId) return;
    if (!resolvedEntryId) return;
    void navigate({
      to: "/$league/groups",
      params: { league: league.slug },
      search: { entry: resolvedEntryId },
      replace: true,
    });
  }, [entriesReady, entryId, resolvedEntryId, navigate, league.slug]);
  useEffect(() => {
    if (!entriesReady) return;
    if (isOverride) return;
    if (resolvedEntryId) return;
    void navigate({
      to: "/$league/entries/new",
      params: { league: league.slug },
      replace: true,
    });
  }, [entriesReady, isOverride, resolvedEntryId, navigate, league.slug]);

  // Remember last-viewed entry for this league (self mode only).
  useEffect(() => {
    if (isOverride) return;
    const id = entryId ?? resolvedEntryId;
    if (!id) return;
    setLastEntryId(league.id, id);
  }, [isOverride, entryId, resolvedEntryId, league.id]);

  // Post-group-lock, the groups editor has nothing left to do — self users
  // get the review experience on the member page (mirrors the /bracket
  // post-lock redirect). Super admins in override mode keep the editor.
  const isPostGroupLock =
    !!league.groupStageLockAt &&
    new Date(league.groupStageLockAt) <= new Date();
  const redirectEntryId = entryId ?? resolvedEntryId;
  useEffect(() => {
    if (!entriesReady) return;
    if (isOverride) return;
    if (!isPostGroupLock) return;
    if (!redirectEntryId) return;
    void navigate({
      to: "/$league/members/$entryId",
      params: { league: league.slug, entryId: redirectEntryId },
      search: { tab: "groups" },
      replace: true,
    });
  }, [entriesReady, isOverride, isPostGroupLock, redirectEntryId, navigate, league.slug]);



  if (!isGroupFormat) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold mb-2">Not available</h1>
        <p className="text-muted-foreground mb-4">
          This league doesn't have a group stage.
        </p>
        <Button asChild variant="outline">
          <Link to="/$league" params={{ league: league.slug }}>
            Back to league
          </Link>
        </Button>
      </main>
    );
  }

  const finalEntryId = entryId ?? resolvedEntryId;
  if (!entriesReady || !finalEntryId || (isPostGroupLock && !isOverride)) {
    return (
      <main className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Loading group picks…
      </main>
    );
  }
  return <GroupsEditor entryId={finalEntryId} />;
}

function GroupsEditor({ entryId }: { entryId: string }) {
  const league = useLeagueStrict();
  const { loading: authLoading } = useAuth();
  const targetUserId = useTargetUserId();
  const isOverride = useIsOverrideMode();
  const targetLabel = league.actingAs?.label ?? "this member";

  const isPostLock =
    !!league.groupStageLockAt &&
    new Date(league.groupStageLockAt) <= new Date();

  const getFn = useAuthedServerFn(getMyGroupPicks);
  const saveFn = useAuthedServerFn(saveMyGroupPicks);
  const clearAllFn = useAuthedServerFn(clearAllMyGroupPicks);
  const standingsFn = useAuthedServerFn(getLeagueGroupStandings);

  // groupId -> { matches, standingsByTeam }
  const [standingsByGroup, setStandingsByGroup] = useState<
    Record<string, { matches: GroupMatchPublic[]; rows: StandingsRow[] }>
  >({});
  const [pointsPerCorrect, setPointsPerCorrect] = useState<number>(1);

  const [data, setData] = useState<{
    groups: UserGroupRow[];
    isLocked: boolean;
    lockedAt: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideMode, setOverrideMode] = useState<"save" | "clear">("save");

  // Clear-all confirmation dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);

  // Override-mode preflight (see BracketScreen for full rationale).
  const listEntriesFn = useAuthedServerFn(listLeagueEntries);
  const [overrideHasNoEntries, setOverrideHasNoEntries] = useState<boolean | null>(
    isOverride ? null : false,
  );
  useEffect(() => {
    if (!isOverride || !targetUserId) {
      setOverrideHasNoEntries(false);
      return;
    }
    let cancelled = false;
    listEntriesFn({ data: { leagueId: league.id } })
      .then((rows) => {
        if (cancelled) return;
        const count = rows.filter((r) => r.ownerUserId === targetUserId).length;
        setOverrideHasNoEntries(count === 0);
      })
      .catch(() => {
        if (cancelled) return;
        setOverrideHasNoEntries(false);
      });
    return () => {
      cancelled = true;
    };
  }, [league.id, isOverride, targetUserId, listEntriesFn]);

  // Per-group local working state (groupId -> picks)
  const [picksByGroup, setPicksByGroup] = useState<Record<string, PicksMap>>({});
  // Last server-confirmed snapshot per group for dirty diffing
  const serverSnapshotRef = useRef<Record<string, PicksMap>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFn({ data: { leagueId: league.id, targetUserId, entryId } });

      // Merge: keep local edits for groups the user has changed, refresh others.
      setPicksByGroup((prev) => {
        const next: Record<string, PicksMap> = {};
        for (const g of res.groups) {
          const serverPicks: PicksMap = { ...EMPTY_PICKS, ...g.picksByPosition };
          const oldServer = serverSnapshotRef.current[g.id];
          const local = prev[g.id];
          const hasLocalDiverge = local && oldServer && !picksEqual(local, oldServer);
          next[g.id] = hasLocalDiverge ? local : serverPicks;
        }
        return next;
      });

      // Update server snapshot AFTER deciding what to keep
      const snap: Record<string, PicksMap> = {};
      for (const g of res.groups) {
        snap[g.id] = { ...EMPTY_PICKS, ...g.picksByPosition };
      }
      serverSnapshotRef.current = snap;

      setData({
        groups: res.groups,
        isLocked: res.isLocked,
        lockedAt: res.lockedAt,
      });
    } catch (e) {
      const m = e instanceof Error ? e.message : "Failed to load groups";
      toast.error(m);
    } finally {
      setLoading(false);
    }
  }, [getFn, league.id, targetUserId, entryId]);

  const reloadStandings = useCallback(async () => {
    try {
      const res = await standingsFn({ data: { leagueId: league.id } });
      setPointsPerCorrect(res.pointsPerCorrectGroupPosition);
      const next: Record<string, { matches: GroupMatchPublic[]; rows: StandingsRow[] }> = {};
      for (const g of res.groups) {
        const rows = computeGroupStandings(
          g.teams,
          g.matches.map((m) => ({
            homeTeamId: m.homeTeamId,
            awayTeamId: m.awayTeamId,
            homeScore: m.homeScore,
            awayScore: m.awayScore,
          })),
        );
        next[g.groupId] = { matches: g.matches, rows };
      }
      setStandingsByGroup(next);
    } catch {
      // Standings are an enhancement; silent fail keeps picks UI usable.
    }
  }, [standingsFn, league.id]);

  useEffect(() => {
    if (authLoading) return;
    void reload();
    void reloadStandings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league.id, authLoading]);

  // Bumped after each successful autosave so the dirtyGroupIds memo
  // re-evaluates against the freshly-mutated serverSnapshotRef.
  const [savedTick, setSavedTick] = useState(0);

  // Compute dirty groups
  const dirtyGroupIds = useMemo(() => {
    const ids: string[] = [];
    for (const [gid, local] of Object.entries(picksByGroup)) {
      const server = serverSnapshotRef.current[gid] ?? EMPTY_PICKS;
      if (!picksEqual(local, server)) ids.push(gid);
    }
    return ids;
    // serverSnapshotRef is a ref; recompute when picksByGroup, data, or
    // savedTick changes (the latter signals a silent autosave finished).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picksByGroup, data, savedTick]);

  const dirtyCount = dirtyGroupIds.length;

  const { filledSlots, totalSlots } = useMemo(() => {
    const groups = data?.groups ?? [];
    const total = groups.length * 4;
    let filled = 0;
    for (const g of groups) {
      const p = picksByGroup[g.id];
      if (!p) continue;
      for (const pos of [1, 2, 3, 4] as const) if (p[pos]) filled++;
    }
    return { filledSlots: filled, totalSlots: total };
  }, [data, picksByGroup]);
  const progressPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;
  const isLocked = data?.isLocked ?? false;

  // Frozen at T-3s (UX nudge — server lock check is the real gate)
  const [frozen, setFrozen] = useState(false);
  // Effective lock: server lock OR client freeze (admins can keep editing post-lock).
  const effectiveLocked = isLocked || (frozen && !isOverride);

  // Warn before navigating away with unsaved changes (in-app + hard unload).
  // Suppress while loading — picks-vs-snapshot diff can be transiently
  // non-zero during hydration and would otherwise pop a phantom dialog.
  useUnsavedChangesGuard(
    !loading && dirtyCount > 0 && !isLocked,
    isOverride && isPostLock
      ? `You have unsaved picks in ${dirtyCount} ${dirtyCount === 1 ? "group" : "groups"}. Leave anyway?`
      : () => autosave.flush(),
  );


  const updateGroupPicks = useCallback(
    (groupId: string, updater: (prev: PicksMap) => PicksMap) => {
      if (effectiveLocked) return;
      setPicksByGroup((prev) => ({
        ...prev,
        [groupId]: updater(prev[groupId] ?? { ...EMPTY_PICKS }),
      }));
    },
    [effectiveLocked],
  );

  // ----- Autosave -----
  // Disabled while loading, when locked for non-admins, and in override-post-lock
  // mode (which still requires the reason+notify dialog).
  const autosaveEnabled =
    !loading && !!data && !isLocked && !(isOverride && isPostLock);

  const picksByGroupRef = useRef(picksByGroup);
  useEffect(() => {
    picksByGroupRef.current = picksByGroup;
  });

  const signature = useMemo(() => sigOfPicks(picksByGroup), [picksByGroup]);
  const initialSignatureRef = useRef("");

  const autosaveSave = useCallback(async () => {
    const current = picksByGroupRef.current;
    const dirty: string[] = [];
    for (const [gid, local] of Object.entries(current)) {
      const server = serverSnapshotRef.current[gid] ?? EMPTY_PICKS;
      if (!picksEqual(local, server)) dirty.push(gid);
    }
    let totalPicks = 0;
    for (const gid of dirty) {
      const local = current[gid] ?? EMPTY_PICKS;
      await saveFn({
        data: {
          leagueId: league.id,
          groupId: gid,
          picks: ([1, 2, 3, 4] as const).map((position) => ({
            position,
            teamId: local[position] ?? null,
          })),
          entryId,
        },
      });
      serverSnapshotRef.current[gid] = { ...local };
      totalPicks += ([1, 2, 3, 4] as const).filter((p) => local[p]).length;
    }
    // Trigger dirtyGroupIds memo to re-evaluate against the updated snapshot.
    if (dirty.length > 0) {
      setSavedTick((t) => t + 1);
      void import("@/lib/analytics").then(({ track }) =>
        track("group_picks_saved", {
          leagueId: league.id,
          leagueSlug: league.slug,
          entryId,
          groupsTouched: dirty.length,
          picksCount: totalPicks,
          source: "autosave",
        }),
      );
    }
  }, [saveFn, league.id, league.slug, entryId]);

  const autosave = useAutosave({
    signature,
    initialSignature: initialSignatureRef.current,
    getPayload: () => null,
    save: autosaveSave,
    enabled: autosaveEnabled,
    onTerminalError: (err) => {
      const msg = err instanceof Error ? err.message : "Save blocked";
      if (msg.toLowerCase().includes("locked")) {
        toast.error("Group picks just locked", {
          description: "Your last change wasn't saved.",
        });
        void reload();
      } else {
        toast.error(msg);
      }
    },
  });

  // After every reload, baseline = current server snapshot signature.
  useEffect(() => {
    if (loading || !data) return;
    const sig = sigOfPicks(serverSnapshotRef.current);
    initialSignatureRef.current = sig;
    autosave.resetBaseline(sig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  useAutosaveAnnouncement(autosaveEnabled);

  // T-3s flush + freeze (UX nudge before server lock)
  useLockApproach(data?.lockedAt ?? null, 3000, autosaveEnabled, () => {
    void autosave.flush();
    if (!isOverride) setFrozen(true);
  });

  const performSave = async (overrideArgs?: { reason: string; notifyUser: boolean }) => {
    setSaving(true);
    const failed: string[] = [];
    const overrideFields = isOverride
      ? {
          targetUserId,
          reason: overrideArgs?.reason,
          notifyUser: overrideArgs?.notifyUser,
        }
      : {};
    try {
      for (const gid of dirtyGroupIds) {
        const local = picksByGroup[gid] ?? EMPTY_PICKS;
        try {
          await saveFn({
            data: {
              leagueId: league.id,
              groupId: gid,
              picks: ([1, 2, 3, 4] as const).map((position) => ({
                position,
                teamId: local[position] ?? null,
              })),
              ...overrideFields,
              entryId,
            },
          });
          serverSnapshotRef.current[gid] = { ...local };
        } catch (e) {
          const groupCode =
            data?.groups.find((g) => g.id === gid)?.groupCode ?? "?";
          failed.push(groupCode);
          const m = e instanceof Error ? e.message : "Failed to save";
          toast.error(`Group ${groupCode}: ${m}`);
        }
      }
      if (failed.length === 0) {
        toast.success(
          dirtyGroupIds.length === 1
            ? "Picks saved"
            : `Saved picks for ${dirtyGroupIds.length} groups`,
        );
      }
      setPicksByGroup((prev) => ({ ...prev }));
      void reload();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (saving) return;
    if (isLocked && !isOverride) return;
    // Override-post-lock still requires the reason+notify dialog.
    if (isOverride && isPostLock) {
      if (dirtyCount === 0) return;
      setOverrideMode("save");
      setOverrideDialogOpen(true);
      return;
    }
    // Pre-lock: flush autosave if anything is pending; otherwise reassure
    // the user that everything is already saved.
    if (dirtyCount === 0 && autosave.status !== "saving" && autosave.status !== "error") {
      toast.success("All picks saved", {
        description: "Goblin's got 'em locked in. 🔒",
      });
      return;
    }
    if (autosave.status === "error") {
      toast.message("Retrying save…");
      autosave.retryNow();
      return;
    }
    await autosave.flush();
  };

  async function handleClearAll(overrideArgs?: { reason: string; notifyUser: boolean }) {
    if (isOverride && isPostLock && !overrideArgs) {
      setOverrideMode("clear");
      setOverrideDialogOpen(true);
      return;
    }
    setClearing(true);
    const overrideFields = isOverride
      ? {
          targetUserId,
          reason: overrideArgs?.reason,
          notifyUser: overrideArgs?.notifyUser,
        }
      : {};
    try {
      await clearAllFn({
        data: { leagueId: league.id, entryId, ...overrideFields },
      });
      // Reset local picks + server snapshot so autosave doesn't re-fire.
      const cleared: Record<string, PicksMap> = {};
      for (const gid of Object.keys(picksByGroup)) {
        cleared[gid] = { ...EMPTY_PICKS };
      }
      setPicksByGroup(cleared);
      for (const gid of Object.keys(serverSnapshotRef.current)) {
        serverSnapshotRef.current[gid] = { ...EMPTY_PICKS };
      }
      setSavedTick((t) => t + 1);
      setClearDialogOpen(false);
      setClearConfirmText("");
      toast.success("Group picks cleared.", {
        description: "Goblin is judging your indecision. 👀",
      });
      void reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to clear group picks";
      toast.error(msg);
    } finally {
      setClearing(false);
    }
  }

  if (isOverride && overrideHasNoEntries === true) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <h1 className="font-display text-xl font-bold text-foreground">
            {targetLabel} hasn't created a bracket yet
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            There are no group picks to edit on their behalf. They need to
            create their first bracket before you can override their picks.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-6 pb-28">
      <div className="mb-3">
        <LeagueDisplaySettings
          leagueId={league.id}
          leagueName={league.name}
          trigger="inline"
        />
      </div>
      <ActiveEntryBanner entryId={entryId} page="groups" />
      <header className="mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-black text-foreground flex items-center gap-2 flex-wrap">
          <Trophy className="w-6 h-6 text-primary" />
          Group Stage Picks
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Predict the order of finish (1st–4th) for every group. Partial picks
          are allowed — anything you leave blank scores 0.
        </p>
        {data?.lockedAt && (
          <div className="mt-3">
            <LockdownCountdown
              lockAt={data.lockedAt}
              timezone={league.bracketLockTimezone}
              variant="banner"
              label="Group picks lock in"
              lockedLabel="Group picks are locked"
            />
          </div>
        )}
        {data?.isLocked && !isOverride && (
          <div className="mt-2 text-xs text-muted-foreground">
            Read-only — your group picks are frozen. Once the Goblin stamps a
            group final, your score for that group locks in too.
          </div>
        )}
        {data && data.groups.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Progress
              </p>
              <p className="font-display text-sm font-bold text-foreground">
                <span className="text-primary">{filledSlots}</span>
                <span className="text-muted-foreground">/{totalSlots}</span>
                {filledSlots === totalSlots && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-success">
                    Complete
                  </span>
                )}
              </p>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  boxShadow: progressPct > 0 ? "var(--shadow-neon)" : undefined,
                }}
              />
            </div>
          </div>
        )}
      </header>

      <FifaRankingLegend className="mb-5" />

      {loading && !data && (
        <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading groups…
        </div>
      )}

      {!loading && data && data.groups.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No groups have been set up yet. Check back once the Goblin has
          finalized group assignments.
        </div>
      )}

      {data && data.groups.length > 0 && (() => {
        const engagements: Record<string, GroupEngagement> = {};
        for (const g of data.groups) {
          const local = picksByGroup[g.id] ?? { ...EMPTY_PICKS };
          engagements[g.id] = computeGroupEngagement({
            picksByPosition: local,
            actualByPosition: g.actualByPosition,
            standings: standingsByGroup[g.id]?.rows,
            pointsPerCorrect,
            groupCode: g.groupCode,
          });
        }
        const summary = summarizeGroupEngagements(Object.values(engagements));
        const allFinal = summary.groupsFinal === summary.groupsTotal;
        const nothingHappened = summary.totalSoFar === 0 && summary.groupsBusted === 0;
        return (
          <>
            {!nothingHappened && (
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-border bg-card/60 px-3 py-2 text-xs">
                <span className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground">
                  Group stage:
                </span>
                <span className="font-bold text-foreground">
                  {summary.finalPoints}{" "}
                  <span className="font-normal text-muted-foreground">final</span>
                </span>
                {!allFinal && (
                  <span className="font-bold text-warning">
                    + {summary.provisionalPoints}{" "}
                    <span className="font-normal text-warning/80">provisional</span>
                  </span>
                )}
                <span className="font-bold text-primary">
                  = {summary.totalSoFar}{" "}
                  <span className="font-normal text-muted-foreground">so far</span>
                </span>
                {summary.groupsBusted > 0 && (
                  <span className="text-destructive">
                    · {summary.groupsBusted} busted 🪦
                  </span>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="What do these numbers mean?"
                      className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 text-xs leading-relaxed">
                    <p className="font-semibold text-foreground mb-1.5">How this updates</p>
                    <ul className="space-y-1.5 text-muted-foreground">
                      <li>
                        <span className="font-semibold text-foreground">Final</span> — points locked in from groups whose 1st–4th finish is mathematically decided. Can't change.
                      </li>
                      <li>
                        <span className="font-semibold text-warning">Provisional</span> — points you'd earn if the current live standings hold. Moves as upsets happen.
                      </li>
                      <li>
                        <span className="font-semibold text-primary">So far</span> — your projected group-stage total right now (final + provisional).
                      </li>
                      <li>
                        <span className="font-semibold text-destructive">Busted 🪦</span> — groups where it's mathematically impossible to gain more points.
                      </li>
                    </ul>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.groups.map((g) => {
                const local = picksByGroup[g.id] ?? { ...EMPTY_PICKS };
                const server =
                  serverSnapshotRef.current[g.id] ??
                  ({ ...EMPTY_PICKS, ...g.picksByPosition } as PicksMap);
                const cardLocked =
                  (data.isLocked && !isOverride) || (frozen && !isOverride);
                return (
                  <GroupCard
                    key={g.id}
                    group={g}
                    isLocked={cardLocked}
                    picks={local}
                    isDirty={!picksEqual(local, server)}
                    onChange={(updater) => updateGroupPicks(g.id, updater)}
                    standings={standingsByGroup[g.id]}
                    engagement={engagements[g.id]}
                  />
                );
              })}
            </div>
          </>
        );
      })()}

      {/* Sticky global Save bar — visible when editable (admins editable post-lock) */}
      {data && data.groups.length > 0 && (!data.isLocked || isOverride) && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background/90 backdrop-blur-md">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-xs sm:text-sm min-w-0 flex items-center gap-3 flex-wrap">
              {isOverride && isPostLock ? (
                <span
                  className={cn(
                    "text-muted-foreground",
                    dirtyCount > 0 && "text-foreground font-medium",
                  )}
                >
                  {dirtyCount === 0
                    ? "All picks saved"
                    : `Unsaved changes in ${dirtyCount} ${dirtyCount === 1 ? "group" : "groups"}`}
                </span>
              ) : (
                <>
                  <AutosaveIndicator
                    status={autosave.status}
                    retryAttempt={autosave.retryAttempt}
                    lastError={autosave.lastError}
                    onRetry={autosave.retryNow}
                  />
                  {dirtyCount > 0 && autosave.status !== "saving" && (
                    <span className="text-muted-foreground hidden sm:inline">
                      ({dirtyCount} {dirtyCount === 1 ? "group" : "groups"})
                    </span>
                  )}
                  {frozen && !isLocked && (
                    <span className="text-warning text-[11px]">
                      Locking — picks frozen
                    </span>
                  )}
                </>
              )}
            </div>
            {isOverride && isPostLock && (
              <Button
                onClick={handleSaveAll}
                disabled={saving || dirtyCount === 0}
                size="sm"
                className="gap-1.5"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? "Saving…" : `Save as ${targetLabel}`}
              </Button>
            )}
          </div>
          {filledSlots > 0 && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                onClick={() => {
                  setClearConfirmText("");
                  setClearDialogOpen(true);
                }}
                disabled={clearing || saving}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3" />
                Clear all group picks
              </button>
            </div>
          )}
          <SaveBarLegalStrip />
        </div>
      )}

      {/* Clear all group picks confirmation dialog */}
      <AlertDialog
        open={clearDialogOpen}
        onOpenChange={(open) => {
          if (clearing) return;
          setClearDialogOpen(open);
          if (!open) setClearConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all your group picks?</AlertDialogTitle>
            <AlertDialogDescription>
              This wipes <strong>every</strong> 1st–4th place pick across{" "}
              <strong>every group</strong>. You can refill anytime before group
              picks lock.
              <br />
              <br />
              Type{" "}
              <span className="font-mono font-bold text-foreground">CLEAR</span>{" "}
              below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            autoFocus
            value={clearConfirmText}
            onChange={(e) => setClearConfirmText(e.target.value)}
            placeholder="Type CLEAR"
            className="font-mono"
            disabled={clearing}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (clearConfirmText.trim() === "CLEAR") void handleClearAll();
              }}
              disabled={clearing || clearConfirmText.trim() !== "CLEAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Clearing…
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Clear all picks
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OverrideSaveDialog
        open={overrideDialogOpen}
        onOpenChange={setOverrideDialogOpen}
        targetLabel={targetLabel}
        scopeLabel={
          overrideMode === "clear"
            ? "group picks clear (all groups)"
            : "group picks"
        }
        onConfirm={async ({ reason, notifyUser }) => {
          if (overrideMode === "clear") {
            await handleClearAll({ reason, notifyUser });
          } else {
            await performSave({ reason, notifyUser });
          }
        }}
      />
    </main>
  );
}

function GroupCard({
  group,
  isLocked,
  picks,
  isDirty,
  onChange,
  standings,
  engagement,
}: {
  group: UserGroupRow;
  isLocked: boolean;
  picks: PicksMap;
  isDirty: boolean;
  onChange: (updater: (prev: PicksMap) => PicksMap) => void;
  standings?: { matches: GroupMatchPublic[]; rows: StandingsRow[] };
  engagement?: GroupEngagement;
}) {
  const [showMatches, setShowMatches] = useState(false);
  const teamById = useMemo(() => {
    const m = new Map<string, UserGroupTeam>();
    for (const t of group.teams) m.set(t.teamId, t);
    return m;
  }, [group.teams]);

  const assigned = useMemo(() => {
    const s = new Set<string>();
    for (const id of Object.values(picks)) if (id) s.add(id);
    return s;
  }, [picks]);

  // Provisional position by team — only meaningful if any match has been played
  const groupPlayedAny = (standings?.rows ?? []).some((r) => r.gp > 0);
  const provisionalPosByTeam = useMemo(() => {
    const m = new Map<string, number>();
    if (!standings) return m;
    for (const r of standings.rows) {
      if (r.provisionalPosition !== null) m.set(r.teamId, r.provisionalPosition);
    }
    return m;
  }, [standings]);
  const gpByTeam = useMemo(() => {
    const m = new Map<string, number>();
    if (!standings) return m;
    for (const r of standings.rows) m.set(r.teamId, r.gp);
    return m;
  }, [standings]);

  // Provisional correctness now lives inside the engagement object the
  // parent computes; this card just renders it via <EngagementPill>.

  const handleTeamClick = (teamId: string) => {
    if (isLocked) return;
    onChange((prev) => {
      const next = { ...prev };
      const existingPos = ([1, 2, 3, 4] as const).find((p) => next[p] === teamId);
      if (existingPos !== undefined) {
        next[existingPos] = null;
        return next;
      }
      const target = ([1, 2, 3, 4] as const).find((p) => next[p] === null);
      if (target === undefined) {
        toast.message("All four slots are filled — tap one to clear it first.");
        return prev;
      }
      next[target] = teamId;
      return next;
    });
  };

  const clearSlot = (pos: number) => {
    if (isLocked) return;
    onChange((prev) => ({ ...prev, [pos]: null }));
  };

  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-4 transition-colors",
        isDirty ? "border-primary/50" : "border-border",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2 flex-wrap">
        <h3 className="font-display text-lg font-bold text-foreground">
          Group {group.groupCode}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {engagement && <EngagementPill engagement={engagement} />}
          {isDirty && !isLocked && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">
              Unsaved
            </span>
          )}
        </div>
      </div>
      {engagement && engagement.state !== "pending" && (
        <p className="-mt-1 mb-1 text-[11px]">
          {engagement.state === "final" ? (
            <>
              <span className="font-semibold text-success">
                Final: {engagement.points} pts
              </span>
              {engagement.predictedPoints !== null &&
                engagement.predictedPoints !== engagement.points && (
                  <span className="text-muted-foreground">
                    {" "}
                    (you were trending {engagement.predictedPoints})
                  </span>
                )}
            </>
          ) : (
            <span className="italic text-warning">
              Trending: {engagement.points} pts
            </span>
          )}
        </p>
      )}
      {engagement && (
        <p className="mb-3 text-[10px] italic text-muted-foreground">
          {engagement.goblinLine}
        </p>
      )}

      {/* Slots */}
      <div className="space-y-1.5 mb-3">
        {([1, 2, 3, 4] as const).map((pos) => {
          const teamId = picks[pos];
          const team = teamId ? teamById.get(teamId) ?? null : null;
          const actualId = group.actualByPosition[pos] ?? null;
          const actualTeam = actualId ? teamById.get(actualId) ?? null : null;
          const hasActual = actualId !== null;
          const correct = hasActual && teamId !== null && teamId === actualId;
          const wrong = hasActual && teamId !== null && teamId !== actualId;

          // Provisional coloring (only when group has started AND not finalized)
          const provPos = teamId ? provisionalPosByTeam.get(teamId) ?? null : null;
          const teamGp = teamId ? gpByTeam.get(teamId) ?? 0 : 0;
          const provMatches =
            !hasActual && groupPlayedAny && teamId !== null && provPos === pos;
          const provMismatch =
            !hasActual && groupPlayedAny && teamId !== null && provPos !== null && provPos !== pos && teamGp > 0;
          const provGray =
            !hasActual && groupPlayedAny && teamId !== null && teamGp === 0;

          return (
            <div
              key={pos}
              className={cn(
                "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
                correct
                  ? "border-success/40 bg-success/10"
                  : wrong
                    ? "border-destructive/40 bg-destructive/10"
                    : provMatches
                      ? "border-success/30 bg-success/5"
                      : provMismatch
                        ? "border-warning/40 bg-warning/10"
                        : provGray
                          ? "border-muted bg-muted/30"
                          : team
                            ? "border-primary/40 bg-primary/5"
                            : "border-dashed border-border bg-muted/20",
              )}
            >
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground w-7">
                {POSITION_LABEL[pos]}
              </span>
              {team ? (
                <>
                  <Flag code={team.code} size={16} />
                  <span className="text-sm font-semibold text-foreground flex-1 truncate">
                    {team.name}
                  </span>
                  <RankBadge code={team.code} size="xs" />
                  {correct && <Check className="w-3.5 h-3.5 text-success" />}
                  {wrong && <X className="w-3.5 h-3.5 text-destructive" />}
                  {!correct && !wrong && provMatches && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold text-success"
                      title="Currently matches the live standings"
                    >
                      ✓ prov
                    </span>
                  )}
                  {!correct && !wrong && provMismatch && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold text-warning"
                      title={`Currently in ${provPos === 1 ? "1st" : provPos === 2 ? "2nd" : provPos === 3 ? "3rd" : "4th"} provisionally`}
                    >
                      now {provPos === 1 ? "1st" : provPos === 2 ? "2nd" : provPos === 3 ? "3rd" : "4th"}
                    </span>
                  )}
                  {!correct && !wrong && provGray && (
                    <span
                      className="text-[10px] uppercase tracking-wider text-muted-foreground"
                      title="This team hasn't played yet"
                    >
                      0 GP
                    </span>
                  )}
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={() => clearSlot(pos)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={`Clear ${POSITION_LABEL[pos]} pick`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              ) : (
                <span className="text-xs italic text-muted-foreground flex-1">
                  {isLocked ? "No pick" : "Tap a team below…"}
                </span>
              )}
              {hasActual && actualTeam && !correct && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"
                  title={`Actual: ${actualTeam.name}`}
                >
                  Actual:
                  <Flag code={actualTeam.code} size={12} />
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Live standings + match expander */}
      {standings && groupPlayedAny && (
        <div className="mb-3 rounded-md border border-border bg-muted/20 p-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">
            Live standings
          </div>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left">
                <th className="font-medium pb-1">#</th>
                <th className="font-medium pb-1">Team</th>
                <th className="font-medium pb-1 text-center">GP</th>
                <th className="font-medium pb-1 text-center">W-D-L</th>
                <th className="font-medium pb-1 text-center">GD</th>
                <th className="font-medium pb-1 text-right">Pts</th>
              </tr>
            </thead>
            <tbody>
              {standings.rows.map((r, i) => {
                const t = teamById.get(r.teamId);
                return (
                  <tr key={r.teamId} className={r.gp === 0 ? "text-muted-foreground/70" : "text-foreground"}>
                    <td className="py-0.5 pr-1 font-mono">{i + 1}</td>
                    <td className="py-0.5 pr-1">
                      <span className="inline-flex items-center gap-1">
                        {t && <Flag code={t.code} size={12} />}
                        <span className="truncate">{t?.name ?? "?"}</span>
                        {t && <RankBadge code={t.code} size="xs" />}
                      </span>
                    </td>
                    <td className="py-0.5 text-center">{r.gp}</td>
                    <td className="py-0.5 text-center">{r.w}-{r.d}-{r.l}</td>
                    <td className="py-0.5 text-center">{r.gd > 0 ? `+${r.gd}` : r.gd}</td>
                    <td className="py-0.5 text-right font-semibold">{r.pts}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {standings && standings.matches.length > 0 && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowMatches((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            {showMatches ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showMatches ? "Hide matches" : `View ${standings.matches.length} matches`}
          </button>
          {showMatches && (
            <ul className="mt-2 space-y-1">
              {standings.matches.map((m) => {
                const home = teamById.get(m.homeTeamId);
                const away = teamById.get(m.awayTeamId);
                const played = m.homeScore !== null && m.awayScore !== null;
                return (
                  <li
                    key={m.id}
                    className="rounded border border-border bg-background/50 px-2 py-1 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground w-6">
                        MD{m.matchDay}
                      </span>
                      <span className="inline-flex items-center gap-1 flex-1">
                        {home && <Flag code={home.code} size={12} />}
                        <span className="truncate">{home?.name}</span>
                      </span>
                      <span className="font-mono font-semibold tabular-nums">
                        {played ? `${m.homeScore}–${m.awayScore}` : "—"}
                      </span>
                      <span className="inline-flex items-center gap-1 flex-1 justify-end">
                        <span className="truncate text-right">{away?.name}</span>
                        {away && <Flag code={away.code} size={12} />}
                      </span>
                    </div>
                    {played && (m.yellowCards != null || m.redCards != null || m.pksAwarded != null) && (
                      <div className="mt-0.5 ml-8 text-[10px] text-muted-foreground flex gap-2">
                        {m.yellowCards != null && <span>🟨 {m.yellowCards}</span>}
                        {m.redCards != null && <span>🟥 {m.redCards}</span>}
                        {m.pksAwarded != null && <span>PKs {m.pksAwarded}</span>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Teams pool — hidden once locked since picks are frozen */}
      {!isLocked && (
        <div className="flex flex-wrap gap-1.5">
          {group.teams.length === 0 && (
            <span className="text-xs text-muted-foreground italic">
              No teams assigned to this group yet.
            </span>
          )}
          {group.teams.map((t) => {
            const isAssigned = assigned.has(t.teamId);
            return (
              <button
                key={t.teamId}
                type="button"
                onClick={() => handleTeamClick(t.teamId)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                  isAssigned
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-background hover:border-primary/40 hover:bg-primary/5",
                )}
                title={
                  isAssigned
                    ? "Tap to remove from your picks"
                    : "Tap to add to next empty slot"
                }
              >
                <Flag code={t.code} size={14} />
                <span className="font-medium">{t.name}</span>
                <RankBadge code={t.code} size="xs" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EngagementPill({ engagement }: { engagement: GroupEngagement }) {
  const cls =
    engagement.state === "final" || engagement.state === "on_track"
      ? "bg-success/15 text-success"
      : engagement.state === "at_risk"
        ? "bg-warning/15 text-warning"
        : engagement.state === "busted"
          ? "bg-destructive/15 text-destructive"
          : "bg-muted/40 text-muted-foreground";
  return (
    <span
      className={cn(
        "text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded whitespace-nowrap",
        cls,
      )}
    >
      {engagement.label}
    </span>
  );
}
